import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Eye, History, Printer, Wallet } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { HomeContentContainer } from '@/components'
import { toast } from '@/components/common/Toast'
import { useConfirmSaveModal } from '@/components/common/Modals/ConfirmSaveModal'
import { TabsSelect } from '@/components/common/TabsSelect'
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslations } from '@/hooks/useTranslations'
import { useGetCustomerNoPage } from '@/modules/customer/services/customer'
import {
  printBulkPaymentPdf,
  useCommitBulkBillPayment,
  useGetOutstandingByCustomer,
  usePreviewBulkBillPayment,
  type BulkPaymentAllocation,
} from '@/modules/bill/services/bill'
import { useInitialRequiredLocation } from '@/hooks/useInitialLocationFilter'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { formatNumber } from '@/utils/number'

import { AllocationPreviewTable } from './AllocationPreviewTable'

export function BulkSettlePage() {
  const t = useTranslations('admin.outstanding_management.bulk_settle')
  const tOutstanding = useTranslations('admin.outstanding_management')
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { tab?: string; location?: string }
  const tab = searchParams.tab === 'sell' ? 'sell' : 'buy'
  const [location, setLocation, { isLoading: isLocationLoading }] = useInitialRequiredLocation(searchParams.location)

  const [customerId, setCustomerId] = useState('')
  const [paidAmountInput, setPaidAmountInput] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'transfer'>('cash')
  const [paymentNote, setPaymentNote] = useState('')
  const [previewAllocations, setPreviewAllocations] = useState<BulkPaymentAllocation[]>([])

  const { data: customerList } = useGetCustomerNoPage()
  const { data: outstandingData, isPending: isOutstandingLoading } = useGetOutstandingByCustomer(
    customerId || undefined,
    tab,
    location,
  )
  const $preview = usePreviewBulkBillPayment()
  const $commit = useCommitBulkBillPayment()
  const { ConfirmSaveModal, showConfirmSaveModal } = useConfirmSaveModal()

  const customers = customerList?.data ?? []
  const outstanding = outstandingData?.data
  const bills = outstanding?.bills ?? []

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId),
    [customers, customerId],
  )

  const syncSearch = (nextTab: string, nextLocation: BillLocationValue) => {
    navigate({
      to: '/admin/outstanding/settle',
      search: { tab: nextTab, location: nextLocation },
    })
  }

  useEffect(() => {
    if (isLocationLoading || searchParams.location) return
    navigate({
      to: '/admin/outstanding/settle',
      search: { tab, location },
      replace: true,
    })
  }, [isLocationLoading, searchParams.location, tab, location, navigate])

  const handleTabChange = (value: string) => {
    setPreviewAllocations([])
    syncSearch(value, location)
  }

  const handleLocationChange = (value: BillLocationValue) => {
    setLocation(value)
    setPreviewAllocations([])
    syncSearch(tab, value)
  }

  const buildPaymentPayload = () => {
    const amount = Number(paidAmountInput)
    if (!customerId) {
      toast.toastFailed(t('select_customer_required'))
      return null
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.toastFailed(t('amount_required'))
      return null
    }
    return {
      customer_id: customerId,
      bill_type: tab,
      location,
      paid_amount: amount,
      payment_method: paymentMethod,
      note: paymentNote.trim() || undefined,
    }
  }

  const handlePreview = async () => {
    const payload = buildPaymentPayload()
    if (!payload) return
    try {
      const res = await $preview.mutateAsync(payload)
      setPreviewAllocations(res.data?.allocations ?? [])
      toast.toastSuccess(t('preview_success'))
    } catch (e) {
      setPreviewAllocations([])
      if (e instanceof Error) toast.toastFailed(e.message)
    }
  }

  const handleCommit = async () => {
    const payload = buildPaymentPayload()
    if (!payload) return
    if (!previewAllocations.length) {
      toast.toastFailed(t('preview_first'))
      return
    }
    try {
      const res = await $commit.mutateAsync(payload)
      const batchId = res.data?.batch_id
      toast.toastSuccess(t('commit_success'))
      setPreviewAllocations([])
      setPaidAmountInput('')
      setPaymentNote('')
      if (batchId) {
        await printBulkPaymentPdf(batchId)
      }
      navigate({ to: '/admin/outstanding', search: { tab, location } })
    } catch (e) {
      if (e instanceof Error) toast.toastFailed(e.message)
    }
  }

  return (
    <HomeContentContainer title={t('title')} subtitle={t('subtitle')}>
      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate({ to: '/admin/outstanding', search: { tab, location } })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          {t('back')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate({ to: '/admin/outstanding/history' })}
        >
          <History className="mr-2 h-4 w-4" aria-hidden />
          {tOutstanding('btn_bulk_history')}
        </Button>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('step_customer')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>{t('bill_type')}</Label>
              <TabsSelect
                value={tab}
                onChange={handleTabChange}
                color={tab === 'buy' ? 'bg-red-500' : 'bg-s-brand-secondary'}
                options={[
                  { name: 'ซื้อ', value: 'buy' },
                  { name: 'ขาย', value: 'sell' },
                ]}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('location')}</Label>
              <Select value={location} onValueChange={(v) => handleLocationChange(v as BillLocationValue)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILL_LOCATION_VALUES.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {BILL_LOCATION_LABELS[loc]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t('location_hint')}</p>
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label>{t('customer')}</Label>
              <Select value={customerId} onValueChange={(v) => {
                setCustomerId(v)
                setPreviewAllocations([])
              }}>
                <SelectTrigger>
                  <SelectValue placeholder={t('customer_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                      {c.phone_number ? ` · ${c.phone_number}` : ''}
                      {c.car_registration ? ` · ${c.car_registration}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {customerId ? (
              <div className="sm:col-span-2 flex flex-wrap gap-4 text-sm">
                <span>
                  {t('location')}: <strong>{BILL_LOCATION_LABELS[location]}</strong>
                </span>
                <span>
                  {t('bill_count')}: <strong>{outstanding?.bill_count ?? 0}</strong>
                </span>
                <span>
                  {t('total_outstanding')}:{' '}
                  <strong>{formatNumber(outstanding?.total_outstanding ?? 0)} บาท</strong>
                </span>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {customerId ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('outstanding_bills')}</CardTitle>
            </CardHeader>
            <CardContent>
              {isOutstandingLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
              ) : bills.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('no_outstanding')}</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('col_bill_number')}</TableHead>
                        <TableHead>{t('col_bill_date')}</TableHead>
                        <TableHead className="text-right">{t('col_total')}</TableHead>
                        <TableHead className="text-right">{t('col_remaining')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bills.map((b) => (
                        <TableRow key={b.bill_id}>
                          <TableCell>{b.bill_number ?? '—'}</TableCell>
                          <TableCell>{b.bill_date}</TableCell>
                          <TableCell className="text-right">{formatNumber(b.total_amount)}</TableCell>
                          <TableCell className="text-right">{formatNumber(b.remaining_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}

        {customerId && bills.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('step_payment')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 max-w-lg">
              <div className="grid gap-2">
                <Label htmlFor="paid_amount">{t('paid_amount')}</Label>
                <Input
                  id="paid_amount"
                  type="number"
                  min={0.01}
                  step={0.01}
                  max={outstanding?.total_outstanding}
                  value={paidAmountInput}
                  onChange={(e) => {
                    setPaidAmountInput(e.target.value)
                    setPreviewAllocations([])
                  }}
                  placeholder={String(outstanding?.total_outstanding ?? '')}
                />
              </div>
              <div className="grid gap-2">
                <Label>{t('payment_method')}</Label>
                <Select
                  value={paymentMethod}
                  onValueChange={(v) => setPaymentMethod(v as 'cash' | 'transfer')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                    <SelectItem value="transfer">{t('transfer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="bulk_note">{t('note')}</Label>
                <Input
                  id="bulk_note"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder={t('note_placeholder')}
                />
              </div>
              <p className="text-sm text-muted-foreground">{t('commit_print_hint')}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={$preview.isLoading}
                  onClick={() => void handlePreview()}
                >
                  <Eye className="mr-2 h-4 w-4" aria-hidden />
                  {t('preview_btn')}
                </Button>
                <Button
                  type="button"
                  disabled={$commit.isLoading || !previewAllocations.length}
                  onClick={() => showConfirmSaveModal()}
                >
                  <Printer className="mr-2 h-4 w-4" aria-hidden />
                  {t('commit_btn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {previewAllocations.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Wallet className="h-4 w-4" aria-hidden />
                {t('preview_title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AllocationPreviewTable allocations={previewAllocations} />
            </CardContent>
          </Card>
        ) : null}
      </div>

      <ConfirmSaveModal
        title={t('confirm_title')}
        description={t('confirm_description', {
          amount: formatNumber(Number(paidAmountInput) || 0),
          customer: selectedCustomer?.name ?? '',
        })}
        onConfirm={handleCommit}
      />
    </HomeContentContainer>
  )
}
